import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCountry } from '../context/CountryContext';
import {
  getProviderApplications, getProviderApplication, approveProviderApplication, rejectProviderApplication,
} from '../api/admin';
import ReviewDocumentList from '../components/ReviewDocumentList';

const when = (v) => (v ? new Date(v).toLocaleString() : '—');

const TYPE_LABEL = {
  driver: 'Driver',
  bodaboda: 'Boda-Boda / Scooter',
  hotel_host: 'Hotel / Vacation Rental Host',
  event_host: 'Wedding Hall / Event Host',
};

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const STATUS_STYLE = {
  pending: { background: '#fef9c3', color: '#854d0e' },
  approved: { background: '#dcfce7', color: '#166534' },
  rejected: { background: '#fee2e2', color: '#991b1b' },
};

// Kenya provider applications (Kenya Registration Plan). `scope` narrows the
// page: "accommodation" shows only Hotel / Vacation Rental and Wedding Hall /
// Event hosts (Accommodation/Providers › Host Applications); without it all
// provider types are listed. Each application opens to show the applicant
// and every submitted document; approval needs all required documents.
export default function ProviderApplications({ scope }) {
  const typeFilters = scope === 'accommodation' ? ['all', 'hotel_host', 'event_host'] : ['all', 'driver', 'bodaboda', 'hotel_host', 'event_host'];
  const { country, availableOptions, ready: countryReady } = useCountry();
  // Filters live in the URL (Kayd, 19.10.2026): they survive a page refresh
  // and can be shared as a link. Unknown values fall back to the defaults.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = STATUS_TABS.some((t) => t.key === searchParams.get('status')) ? searchParams.get('status') : 'pending';
  const typeFilter = typeFilters.includes(searchParams.get('type')) ? searchParams.get('type') : 'all';
  const setFilter = (key, value, fallback) => {
    const next = new URLSearchParams(searchParams);
    if (value === fallback) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const setStatusFilter = (v) => setFilter('status', v, 'pending');
  const setTypeFilter = (v) => setFilter('type', v, 'all');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const requestSeq = useRef(0);

  // silent: refresh the counts without hiding the list (used after a single
  // document decision, so the open application stays open).
  const load = async ({ silent = false } = {}) => {
    // Only the latest request may update the list: a slower answer to an
    // earlier click must never overwrite the result of the newest filter.
    const seq = ++requestSeq.current;
    if (!silent) setLoading(true);
    try {
      const params = { status: statusFilter, limit: 50 };
      if (typeFilter !== 'all') params.account_type = typeFilter;
      else if (scope === 'accommodation') params.account_types = 'hotel_host,event_host';
      if (country && country !== 'ALL' && country !== 'NONE') params.country = country;
      const res = await getProviderApplications(params);
      if (seq !== requestSeq.current) return;
      setRows(res.data.applications || []);
      setTotal(res.data.pagination?.total ?? (res.data.applications || []).length);
      setError('');
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setRows([]);
      setTotal(0);
      setError(e.response?.data?.error || 'Could not load applications.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => { if (!countryReady) return; setOpenId(null); load(); }, [typeFilter, statusFilter, country, countryReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const countryName = country === 'ALL' ? 'all countries' : (availableOptions.find((o) => o.code === country)?.label || country);
  const statusLabel = statusFilter === 'all' ? '' : `${STATUS_TABS.find((t) => t.key === statusFilter)?.label.toLowerCase()} `;
  const typeLabel = typeFilter === 'all' ? '' : ` from ${TYPE_LABEL[typeFilter]}s`;

  const title = scope === 'accommodation' ? 'Host Applications' : 'Provider Applications';
  const subtitle = scope === 'accommodation'
    ? 'Applications from Hotel / Vacation Rental hosts and Wedding Hall / Event hosts (Kenya). Open an application to review the documents, then approve or reject it.'
    : 'Kenya provider applications: Driver, Boda-Boda / Scooter, Hotel / Vacation Rental host, Wedding Hall / Event host.';

  return (
    <div style={s.page}>
      <h1 style={s.h1}>{title}</h1>
      <p style={s.sub}>{subtitle}</p>

      <div style={s.filterRow} role="group" aria-label="Status">
        <span style={s.filterLabel}>Status</span>
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => setStatusFilter(t.key)} aria-pressed={statusFilter === t.key} style={{ ...s.pill, ...(statusFilter === t.key ? s.pillActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={s.filterRow} role="group" aria-label="Host type">
        <span style={s.filterLabel}>Type</span>
        {typeFilters.map((t) => (
          <button key={t} onClick={() => setTypeFilter(t)} aria-pressed={typeFilter === t} style={{ ...s.pill, ...(typeFilter === t ? s.pillActive : {}) }}>
            {t === 'all' ? 'All types' : TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      <p style={s.summary} aria-live="polite">
        {loading ? 'Loading…' : `${total} ${statusLabel}application${total === 1 ? '' : 's'}${typeLabel} · ${countryName}`}
      </p>

      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
      {!loading && !error && rows.length === 0 ? (
        <div style={s.empty}>
          No {statusLabel}applications{typeLabel} in {countryName}.
          {country !== 'ALL' && <div style={s.emptyHint}>Choose "All countries" or another country in the header to see more.</div>}
        </div>
      ) : loading ? null : (
        <div style={s.list}>
          {rows.map((a) => (
            <ApplicationCard
              key={a.id}
              application={a}
              open={openId === a.id}
              onToggle={() => setOpenId(openId === a.id ? null : a.id)}
              onChanged={() => { setOpenId(null); load(); }}
              onRefresh={() => load({ silent: true })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationCard({ application: a, open, onToggle, onChanged, onRefresh }) {
  const u = a.users || {};
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const complete = a.documents_required > 0 && a.documents_uploaded === a.documents_required;

  const loadDetail = () => getProviderApplication(a.id)
    .then((res) => setDetail(res.data))
    .catch((e) => toast.error(e.response?.data?.error || 'Could not load the application'));
  useEffect(() => {
    if (!open) return;
    setLoadingDetail(true);
    loadDetail().finally(() => setLoadingDetail(false));
  }, [open, a.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    setBusy(true);
    try {
      await approveProviderApplication(a.id);
      toast.success('Application approved — account activated');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Approval failed');
    } finally { setBusy(false); }
  };
  const reject = async (reason) => {
    setBusy(true);
    try {
      await rejectProviderApplication(a.id, reason);
      toast.success('Application rejected');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Rejection failed');
    } finally { setBusy(false); }
  };

  const missing = detail?.missing_documents || [];
  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <div style={{ minWidth: 0 }}>
          <div style={s.name}>
            {u.full_name || u.email}
            {u.customer_number && <span style={s.ref}> · #{u.customer_number}</span>}
          </div>
          <div style={s.meta}>{TYPE_LABEL[a.account_type] || a.account_type} · {u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
          <div style={s.meta}>Applied {when(a.created_at)}{a.reviewed_at ? ` · reviewed ${when(a.reviewed_at)}` : ''}</div>
        </div>
        <div style={s.headSide}>
          <span style={{ ...s.badge, ...(STATUS_STYLE[a.status] || {}) }}>{a.status}</span>
          <span style={{ ...s.badge, ...(complete ? STATUS_STYLE.approved : STATUS_STYLE.rejected) }}>
            Documents {a.documents_uploaded}/{a.documents_required}
          </span>
          <button style={s.btnLight} onClick={onToggle} aria-expanded={open}>{open ? 'Close' : 'Review'}</button>
        </div>
      </div>

      {a.status === 'rejected' && a.admin_notes && <p style={s.reason}>Rejection reason: {a.admin_notes}</p>}

      {open && (
        <div style={s.detail}>
          {loadingDetail || !detail ? <p style={s.meta}>Loading documents…</p> : (
            <>
              {detail.personal?.length > 0 && (
                <>
                  <h3 style={s.h3}>Personal details (Vehicle Lessor Requirements → Driver)</h3>
                  <div style={{ ...s.facts, marginBottom: 14 }}>
                    {detail.personal.map((f) => <Fact key={f.key} label={f.label} value={f.missing ? 'Missing' : String(f.value)} />)}
                  </div>
                </>
              )}
              <div style={s.facts}>
                <Fact label="Market" value={u.home_market || '—'} />
                <Fact label="Phone" value={detail.application.users?.phone || '—'} />
                {/* Shown above already for drivers (personal details). */}
                {!detail.personal?.length && <Fact label="Address" value={detail.application.users?.address || '—'} />}
                {!detail.personal?.length && <Fact label="Date of birth" value={detail.application.users?.date_of_birth || '—'} />}
              </div>
              {/* Vehicle details — driver and boda-boda applications */}
              {['driver', 'bodaboda'].includes(a.account_type) && detail.vehicle && (
                <>
                  <h3 style={s.h3}>Vehicle (as the driver sees it)</h3>
                  <div style={{ ...s.facts, marginBottom: 14 }}>
                    <Fact label="Vehicle" value={[detail.vehicle.make, detail.vehicle.model].filter(Boolean).join(' ') || '—'} />
                    <Fact label="Licence plate" value={detail.vehicle.license_plate || '—'} />
                    <Fact label="Year / seats / colour" value={[detail.vehicle.year, detail.vehicle.seating_capacity && `${detail.vehicle.seating_capacity} seats`, detail.vehicle.color].filter(Boolean).join(' · ') || '—'} />
                    <Fact label="Vehicle review status" value={detail.vehicle.review_status === 'approved' ? 'Approved' : detail.vehicle.review_status === 'rejected' ? 'Rejected' : 'Under review'} />
                  </div>
                  {detail.vehicle_missing_details?.length > 0 && <p style={s.warn}>Missing vehicle details: {detail.vehicle_missing_details.join(', ')}</p>}
                </>
              )}
              {['driver', 'bodaboda'].includes(a.account_type) && !detail.vehicle && (
                detail.application.driver_vehicle_make ||
                detail.application.driver_vehicle_model ||
                detail.application.driver_vehicle_year
              ) && (
                <>
                  <h3 style={s.h3}>Vehicle Details</h3>
                  <div style={{ ...s.facts, marginBottom: 14 }}>
                    <Fact label="Make" value={detail.application.driver_vehicle_make || '—'} />
                    <Fact label="Model / Type" value={detail.application.driver_vehicle_model || '—'} />
                    <Fact label="Seats" value={detail.application.driver_vehicle_seats ? String(detail.application.driver_vehicle_seats) : '—'} />
                    <Fact label="Year" value={detail.application.driver_vehicle_year ? String(detail.application.driver_vehicle_year) : '—'} />
                    <Fact label="Colour" value={detail.application.driver_vehicle_color || '—'} />
                    <Fact label="Number plate" value={detail.application.driver_vehicle_plate || '—'} />
                  </div>
                </>
              )}
              <h3 style={s.h3}>Submitted documents</h3>
              <ReviewDocumentList documents={detail.documents} userId={a.user_id} onChanged={async () => { await loadDetail(); onRefresh && onRefresh(); }} />
              {a.status === 'pending' && (
                <div style={s.actions}>
                  {missing.length > 0 && <p style={s.warn}>Cannot approve yet — missing: {missing.join(', ')}</p>}
                  <button
                    style={{ ...s.btn, ...s.btnGreen, ...(missing.length || busy ? s.disabled : {}) }}
                    disabled={missing.length > 0 || busy}
                    onClick={approve}
                  >
                    Approve
                  </button>
                  <RejectButton onReject={reject} disabled={busy} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div style={s.fact}>
      <div style={s.factLabel}>{label}</div>
      <div style={s.factValue}>{value}</div>
    </div>
  );
}

function RejectButton({ onReject, disabled }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return <button style={{ ...s.btn, ...s.btnRed }} disabled={disabled} onClick={() => setOpen(true)}>Reject</button>;
  }
  return (
    <div style={s.rejectRow}>
      <input
        style={s.input}
        placeholder="Reason (shown to the applicant)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label="Rejection reason"
      />
      <button style={{ ...s.btn, ...s.btnRed }} disabled={!reason.trim() || disabled} onClick={() => onReject(reason.trim())}>Confirm</button>
      <button style={s.btnLight} onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1000 },
  h1: { margin: '0 0 6px', fontSize: 22, fontWeight: 800 },
  sub: { margin: '0 0 16px', color: '#64748b', fontSize: 14, maxWidth: 760 },
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' },
  filterLabel: { fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 48 },
  summary: { fontSize: 13, color: '#475569', margin: '4px 0 10px', fontWeight: 600 },
  empty: { background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: '20px 16px', color: '#475569', fontSize: 14, textAlign: 'center' },
  emptyHint: { fontSize: 12.5, color: '#64748b', marginTop: 6 },
  pill: { padding: '7px 13px', borderRadius: 999, border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 13, background: '#f1f5f9', color: '#334155' },
  pillActive: { background: '#0B6A4A', color: '#fff' },
  list: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 6 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(15,23,42,0.06)' },
  cardHead: { display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
  name: { fontWeight: 700, fontSize: 15 },
  ref: { fontWeight: 500, color: '#64748b', fontSize: 13 },
  meta: { fontSize: 13, color: '#64748b', marginTop: 3, wordBreak: 'break-word' },
  headSide: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  badge: { fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#f1f5f9', color: '#334155', textTransform: 'capitalize' },
  reason: { fontSize: 13, color: '#991b1b', margin: '10px 0 0' },
  detail: { borderTop: '1px solid #f1f5f9', marginTop: 14, paddingTop: 14 },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 },
  fact: { background: '#f8fafc', borderRadius: 8, padding: '8px 10px' },
  factLabel: { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  factValue: { fontSize: 13.5, color: '#0f172a', marginTop: 2, wordBreak: 'break-word' },
  h3: { fontSize: 14, fontWeight: 700, margin: '0 0 8px' },
  actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 14 },
  warn: { flexBasis: '100%', margin: 0, fontSize: 13, color: '#92400e' },
  btn: { padding: '8px 14px', borderRadius: 8, border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  btnGreen: { background: '#0f766e', color: '#fff' },
  btnRed: { background: '#ef4444', color: '#fff' },
  btnLight: { padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#0f172a', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
  rejectRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: '1 1 320px' },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, flex: '1 1 200px', minWidth: 0 },
};
